import React, { useState } from 'react';
import { cameraDisplayName } from './usbMicroscopeCamera';
import { AUTO_FRONT, AUTO_REAR } from './cameraSelectValues';
import './EnterpriseCameraSelect.css';

/**
 * One dropdown for kiosk capture: auto front/rear + every connected camera (incl. USB when listed).
 * Optional Refresh CTA re-enumerates devices after a late USB / microscope connect.
 */
export default function NaturalsCameraSelect({
  label = 'Camera',
  value = '',
  onChange,
  onRefresh,
  devices = [],
  nativeDevices = [],
  includeAutoFacing = true,
  className = '',
  id,
  disabled = false,
}) {
  const [refreshing, setRefreshing] = useState(false);
  const webList = Array.isArray(devices) ? devices.filter((d) => d?.deviceId) : [];
  const nativeList = Array.isArray(nativeDevices) ? nativeDevices : [];
  const canRefresh = typeof onRefresh === 'function';

  const handleRefresh = async () => {
    if (!canRefresh || disabled || refreshing) return;
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className={`naturals-camera-select ${className}`.trim()}>
      <div className="naturals-camera-select__header">
        <label className="naturals-camera-select__label" htmlFor={id}>
          {label}
        </label>
        {canRefresh ? (
          <button
            type="button"
            className="naturals-camera-select__refresh"
            onClick={handleRefresh}
            disabled={disabled || refreshing}
            aria-busy={refreshing}
          >
            {refreshing ? 'Refreshing…' : 'Refresh cameras'}
          </button>
        ) : null}
      </div>
      <select
        id={id}
        className="naturals-camera-select__input"
        value={value}
        disabled={disabled || refreshing}
        onChange={(e) => onChange?.(e.target.value)}
      >
        <option value="">Choose a camera…</option>
        {includeAutoFacing ? (
          <optgroup label="Automatic">
            <option value={AUTO_FRONT}>Front camera (automatic)</option>
            <option value={AUTO_REAR}>Rear camera (automatic)</option>
          </optgroup>
        ) : null}
        {webList.length > 0 ? (
          <optgroup label="Connected cameras">
            {webList.map((d, i) => (
              <option key={d.deviceId || i} value={`device:${d.deviceId}`}>
                {cameraDisplayName(d, i, webList.length)}
              </option>
            ))}
          </optgroup>
        ) : null}
        {nativeList.length > 0 ? (
          <optgroup label="USB microscope (Salon app)">
            {nativeList.map((c) => (
              <option key={String(c.nativeId)} value={`native:${c.nativeId}`}>
                {c.label || `USB camera ${c.nativeId}`}
              </option>
            ))}
          </optgroup>
        ) : null}
      </select>
    </div>
  );
}
